/**
 * The greeting every role's home and Atlas open with (Andrew, 2026-10-19): "Good morning",
 * "Good afternoon" or "Good evening" by the person's own clock, then their first name.
 *
 * Their clock is the facility's time zone when the account sits on a building that has one
 * (GET /auth/me time_zone), else this device's. Morning runs to noon, afternoon to 5 PM
 * (the design's turn to evening), evening after. In Spanish the afternoon runs to 8 PM:
 * "Buenos días" to noon, "Buenas tardes" to 8 PM, "Buenas noches" after (Simon's review,
 * 2026-10-19), so a Spanish screen asks for lang 'es' and is given its own words here, not a
 * translation of the English hour.
 */

export type DayPart = 'morning' | 'afternoon' | 'evening';

/** The hour (0 to 23) on the person's own clock: `timeZone` when it is a real zone, else this device's. */
export function localHour(now: Date, timeZone?: string | null): number {
  if (timeZone) {
    try {
      const h = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', hourCycle: 'h23' }).formatToParts(now).find((p) => p.type === 'hour');
      const n = h ? Number(h.value) : NaN;
      if (Number.isFinite(n)) return n % 24;
    } catch {
      /* not a zone Intl knows: this device's clock */
    }
  }
  return now.getHours();
}

export function dayPart(now: Date, timeZone?: string | null): DayPart {
  const h = localHour(now, timeZone);
  return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
}

const WORD: Record<DayPart, string> = { morning: 'Good morning', afternoon: 'Good afternoon', evening: 'Good evening' };

/** A person's first name from their full name: the first word, or null when there is none. */
export function firstNameOf(name: string | null | undefined): string | null {
  const full = String(name ?? '').trim().replace(/,.*$/, '');
  const w = full.split(/\s+/)[0] ?? '';
  if (!w || /@/.test(w)) return null;
  // An initial ("M. Ortiz") is not a first name: greet by the name as written, never "M.."
  return /^[A-Za-z]\.?$/.test(w) ? full : w;
}

/** The Spanish word for the hour: días to noon, tardes to 8 PM, noches after. */
export function spanishGreetingWord(now: Date, timeZone?: string | null): string {
  const h = localHour(now, timeZone);
  return h < 12 ? 'Buenos días' : h < 20 ? 'Buenas tardes' : 'Buenas noches';
}

/**
 * "Good morning, Ana" (no full stop: the screen adds what follows). With no name, "Good morning".
 * lang 'es': "Buenas tardes, Ana", with the Spanish hours (spanishGreetingWord).
 */
export function greet(name: string | null | undefined, now: Date = new Date(), timeZone?: string | null, lang: 'en' | 'es' = 'en'): string {
  const first = firstNameOf(name);
  const word = lang === 'es' ? spanishGreetingWord(now, timeZone) : WORD[dayPart(now, timeZone)];
  return first ? `${word}, ${first}` : word;
}
